// frontend/src/hooks/useAuth.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Load user on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('dwi_token');
      if (token) {
        try {
          const res = await authAPI.me();
          setUser(res.user);
        } catch {
          localStorage.removeItem('dwi_token');
          localStorage.removeItem('dwi_user');
        }
      }
      setLoading(false);
      setInitialized(true);
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('dwi_token', res.token);
    localStorage.setItem('dwi_user', JSON.stringify(res.user));
    setUser(res.user);
    toast.success(`Welcome back, ${res.user.name.split(' ')[0]}!`);
    return res;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    localStorage.setItem('dwi_token', res.token);
    localStorage.setItem('dwi_user', JSON.stringify(res.user));
    setUser(res.user);
    toast.success('Account created successfully!');
    return res;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('dwi_token');
    localStorage.removeItem('dwi_user');
    setUser(null);
    toast.success('Logged out');
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.user);
      return res.user;
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  const value = {
    user,
    loading,
    initialized,
    isAuthenticated: !!user,
    isAdmin:          user?.role === 'admin',
    isISP:            user?.role === 'isp_owner',
    isReseller:       user?.role === 'reseller',
    isAgent:          ['agent', 'teller'].includes(user?.role),
    isTeller:         user?.role === 'teller',
    isBranchManager:  user?.role === 'branch_manager',
    isMerchant:       user?.role === 'merchant',
    isPartner:        user?.role === 'partner',
    login,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
